import { useCallback, useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import StairPDFDocument from "./StairPDFDocument";

const buttonClass = "pd-btn pd-btn--ghost";
const fileName = "stairs_calculator_results.pdf";

const serialize = (value) => {
  if (value === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    console.warn("[stairs] Failed to serialise data for PDF export", err);
    return null;
  }
};

export default function DownloadStairReportBtn({ project, result, entranceIdx, disabled }) {
  const serializedProject = useMemo(() => serialize(project), [project]);
  const serializedResult = useMemo(() => serialize(result), [result]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  const hasResults = useMemo(
    () =>
      serializedResult &&
      typeof serializedResult === "object" &&
      Array.isArray(serializedResult.floors) &&
      serializedResult.floors.length > 0,
    [serializedResult],
  );

  const exportReady = useMemo(
    () => Boolean(serializedProject && serializedResult && hasResults),
    [hasResults, serializedProject, serializedResult],
  );

  useEffect(() => {
    if (exportReady) setError("");
  }, [exportReady]);

  const handleDownload = useCallback(async () => {
    if (!exportReady || disabled || isGenerating) return;

    setIsGenerating(true);
    setError("");

    try {
      const blob = await pdf(
        <StairPDFDocument
          project={serializedProject}
          result={serializedResult}
          entranceIdx={entranceIdx}
        />,
        {},
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[stairs] PDF export failed", err);
      setError(err?.message || "Export unavailable");
    } finally {
      setIsGenerating(false);
    }
  }, [disabled, entranceIdx, exportReady, isGenerating, serializedProject, serializedResult]);

  const disabledState = disabled || isGenerating || !exportReady;
  const title = error || (!exportReady ? "Run the calculator to export." : "");

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={handleDownload}
      disabled={disabledState}
      aria-disabled={disabledState}
      title={title || undefined}
    >
      {isGenerating ? "Generating…" : "Export PDF"}
    </button>
  );
}
